import { useTranslation } from "react-i18next";
import ArrowLonRightIcon from "../../svg/ArrowLongRightIcon";
// import { formatIBAN, validateIBAN } from '../../utils/ibanHelper';
import { useState } from "react";
import { validateIBAN, electronicFormatIBAN, friendlyFormatIBAN } from "ibantools";

interface BillingFormProps {
  companyData: {
    billingEmail: string;
    billingAddressStreet: string;
    billingAddressNumber: string;
    billingAddressZip: string;
    billingAddressCity: string;
    billingAddressCountry: string;
    invoicingCycle: string;
    iban: string;
  };
  onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onBack: () => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  /**
   * ADDITIVE, and optional so the existing caller (`AddCompanyPage`) is byte-for-byte
   * unchanged when it is omitted. The Customer Switch's inline create passes it to
   * hold the submit disabled for the WHOLE in-flight window: the create is a real,
   * committed, irreversible write, and a second POST landing after the first
   * succeeded produces a confusing "added by someone else" 409 for something the
   * user did themselves.
   */
  submitting?: boolean;
}

export const BillingForm = ({ companyData, onInputChange, onBack, onSubmit, submitting }: BillingFormProps) => {
  const { t } = useTranslation("AddCompany");

  const [ibanError, setIbanError] = useState<string>("");
  const [isIbanValid, setIsIbanValid] = useState(true);

  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    const formattedIBAN = friendlyFormatIBAN(rawValue);

    e.target.value = formattedIBAN ?? "";

    onInputChange(e);

    if (rawValue.length > 0) {
      const electronicIBAN = electronicFormatIBAN(rawValue);

      const validationResult = validateIBAN(electronicIBAN || "");

      if (validationResult.valid) {
        setIbanError("");
        setIsIbanValid(true);
      } else {
        setIbanError(t("invalidIBAN"));
        setIsIbanValid(false);
      }
    } else {
      setIbanError("");
      setIsIbanValid(true);
    }
  };
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-6 font-semibold">
      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">{t("billingEmail")}</p>
          <input
            className="border py-3 px-4 w-full rounded-md"
            type="email"
            name="billingEmail"
            value={companyData.billingEmail}
            onChange={onInputChange}
          />
        </div>
        <div className="flex-1">
          <p className="mb-2">{t("iban")}</p>
          <input
            className={`border py-3 px-4 w-full rounded-md ${ibanError ? "border-red-500" : ""}`}
            type="text"
            name="iban"
            value={companyData.iban}
            onChange={handleIBANChange}
            placeholder="BE68 5390 0754 7034"
          />
          {ibanError && <p className="text-red-500 text-sm mt-1">{ibanError}</p>}
        </div>
      </div>

      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">{t("billingStreet")}</p>
          <input
            className="border py-3 px-4 w-full rounded-md"
            type="text"
            name="billingAddressStreet"
            value={companyData.billingAddressStreet}
            onChange={onInputChange}
          />
        </div>
        <div className="flex-1">
          <p className="mb-2">{t("billingNumber")}</p>
          <input
            className="border py-3 px-4 w-full rounded-md"
            type="text"
            name="billingAddressNumber"
            value={companyData.billingAddressNumber}
            onChange={onInputChange}
          />
        </div>
      </div>

      <div className="flex justify-between gap-8">
        <div className="flex-1">
          <p className="mb-2">{t("billingPostalCode")}</p>
          <input
            className="border py-3 px-4 w-full rounded-md"
            type="text"
            name="billingAddressZip"
            value={companyData.billingAddressZip}
            onChange={onInputChange}
          />
        </div>
        <div className="flex-1">
          <p className="mb-2">{t("billingCity")}</p>
          <input
            className="border py-3 px-4 w-full rounded-md"
            type="text"
            name="billingAddressCity"
            value={companyData.billingAddressCity}
            onChange={onInputChange}
          />
        </div>
      </div>

      {/* <div className='flex justify-between gap-8'>
        <div className='flex-1'>
          <p className='mb-2'>{t('invoicingCycle')}</p>
          <input
            className='border py-3 px-4 w-full rounded-md bg-gray-100'
            type='text'
            name='invoicingCycle'
            value={companyData.invoicingCycle || 'Monthly'}
            readOnly
          />
        </div>
      </div> */}

      <div className="hidden">
        <input type="hidden" name="invoicingCycle" value={companyData.invoicingCycle || "Monthly"} />
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          className="py-3 px-4 rounded-md border border-primary-600 text-primary-600 disabled:opacity-50"
          onClick={onBack}
          disabled={!!submitting}
        >
          {t("back")}
        </button>
        <button
          type="submit"
          className={`py-3 px-4 rounded-md ${
            isIbanValid && !submitting ? "bg-primary-600 hover:bg-primary-700" : "bg-gray-400 cursor-not-allowed"
          } text-white flex gap-3 items-center`}
          disabled={!isIbanValid || !!submitting}
        >
          {t("submit")} <ArrowLonRightIcon />
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-primary-600">
          {t("billingFieldsNote", { defaultValue: "These fields are optional and can be filled later." })}
        </span>
      </div>
    </form>
  );
};
